// Layout tab of the dashboard designer's panel: tips when nothing is selected,
// the selection when several tiles are, and the tile inspector for one tile
// (content, title, id, title bar, position & size, criteria bindings).

import { useMemo } from 'react'
import {
  Move, Maximize2, Copy, ArrowUp, Plus, Trash2, X, Pencil, FileText,
} from 'lucide-react'
import type { ProjectReportSummary, ProjectVisualSummary } from '@/api/designers'
import type {
  DashboardBindType, DashboardCriterionBinding, DashboardGlobalCriterion, DashboardVisual,
} from './types'
import { extractVisualParams, type VisualParam } from './visualParams'
import { Sec, Switch, visualRefToObjName } from './designerShared'
import { humanizeObjName, visualTypeIcon } from './AddVisualModal'

type Box = keyof DashboardVisual['POSITION']

export interface LayoutPanelProps {
  canEdit: boolean
  tiles: DashboardVisual[]
  selectedIds: ReadonlySet<string>
  primary: DashboardVisual | null
  projectVisuals: ProjectVisualSummary[]
  projectReports: ProjectReportSummary[]
  visualContent: unknown | null | undefined
  globalCriteria: DashboardGlobalCriterion[]
  onOpenAdd: () => void
  onOpenReplace: (kind: 'visual' | 'report') => void
  onSetContentKind: (kind: 'visual' | 'report') => void
  onEditVisual: (objName: string) => void
  onFieldChange: <K extends keyof DashboardVisual>(id: string, field: K, value: DashboardVisual[K]) => void
  onBoxChange: (id: string, field: Box, value: number) => void
  onSetBinding: (visualId: string, visualParam: string, binding: DashboardCriterionBinding | null) => void
  onDelete: (ids: string[]) => void
  onClearSelection: () => void
  onGoToCriteria: () => void
}

export default function LayoutPanel(p: LayoutPanelProps) {
  const { canEdit, tiles, selectedIds, primary } = p

  if (selectedIds.size > 1) {
    const sel = tiles.filter((t) => selectedIds.has(t.VISUAL_ID))
    return (
      <div style={{ padding: '8px 20px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div className="dd-ptitle">{sel.length} tiles selected</div>
        <p className="dd-hint" style={{ margin: 0 }}>
          Drag any selected tile to move the group. Arrow keys nudge all of them.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sel.map((t) => (
            <div key={t.VISUAL_ID} className="dd-chk">
              <span className="dd-mono" style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>{t.VISUAL_ID}</span>
              <span className="dd-ell" style={{ fontSize: 14, fontWeight: 500 }}>{t.VISUAL_NAME}</span>
            </div>
          ))}
        </div>
        {canEdit && (
          <button className="dd-tb danger out" style={{ justifyContent: 'center' }} onClick={() => p.onDelete(sel.map((t) => t.VISUAL_ID))}>
            <Trash2 size={15} /> Remove {sel.length} tiles
          </button>
        )}
        <button className="dd-tb out" style={{ justifyContent: 'center' }} onClick={p.onClearSelection}>Clear selection</button>
      </div>
    )
  }

  if (!primary) {
    return (
      <div style={{ padding: '8px 20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div className="dd-ptitle">Select a tile</div>
          <p className="dd-hint" style={{ marginTop: 4 }}>Click a tile on the grid to edit its visual, size and bindings.</p>
        </div>
        <div className="dd-tips">
          <div><span className="dd-vico"><Move size={16} /></span>Drag a tile to move it. Tiles below make room.</div>
          <div><span className="dd-vico"><Maximize2 size={16} /></span>Select it, then drag any edge or corner to resize.</div>
          <div><span className="dd-vico"><Copy size={16} /></span>Ctrl / ⌘ + click to select several and move them together.</div>
          <div><span className="dd-vico"><ArrowUp size={16} /></span>Arrow keys nudge by ¼ cell, Shift + arrow by a whole cell. Delete removes the selection.</div>
        </div>
        {canEdit && (
          <button className="dd-tb pri" style={{ justifyContent: 'center', height: 40 }} onClick={p.onOpenAdd}>
            <Plus size={16} /> Add visual
          </button>
        )}
      </div>
    )
  }

  return <TileInspector {...p} tile={primary} />
}

function TileInspector(p: LayoutPanelProps & { tile: DashboardVisual }) {
  const { tile, canEdit } = p
  const isReport = tile.TILE_KIND === 'report'
  const params = useMemo(() => (p.visualContent ? extractVisualParams(p.visualContent) : []), [p.visualContent])
  const byParam = useMemo(() => {
    const m = new Map<string, DashboardCriterionBinding>()
    for (const b of tile.CRITERIA_BINDINGS || []) m.set(b.VISUAL_PARAM, b)
    return m
  }, [tile.CRITERIA_BINDINGS])
  const wired = params.filter((x) => (byParam.get(x.name)?.BIND_TYPE || 'unbound') !== 'unbound').length
  const visualObj = visualRefToObjName(tile.VISUAL_CONFIG_REF || '')
  const visualInfo = p.projectVisuals.find((v) => v.filename === tile.VISUAL_CONFIG_REF)
  const reportInfo = p.projectReports.find((r) => r.obj_name === tile.REPORT_OBJ)
  const ref = isReport ? tile.REPORT_OBJ : tile.VISUAL_CONFIG_REF
  const contentName = isReport
    ? (tile.REPORT_OBJ ? (reportInfo?.title || humanizeObjName(tile.REPORT_OBJ)) : 'No report picked')
    : (tile.VISUAL_CONFIG_REF ? humanizeObjName(visualObj) : 'No visual picked')
  const contentFile = isReport
    ? (tile.REPORT_OBJ || 'Choose one with Change')
    : tile.VISUAL_CONFIG_REF
      ? `${tile.VISUAL_CONFIG_REF}${visualInfo?.visual_type ? ` · ${visualInfo.visual_type}` : visualInfo ? '' : ' · not found in this project'}`
      : 'Choose one with Change'

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 12px 12px 16px' }}>
        <div style={{ minWidth: 0, flex: '1 1 auto' }}>
          <div className="dd-mono" style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>{tile.VISUAL_ID}</div>
          <div className="dd-ell" style={{ fontSize: 16, fontWeight: 650, marginTop: 1 }}>{tile.VISUAL_NAME || tile.VISUAL_ID}</div>
        </div>
        {canEdit && (
          <button className="dd-tb danger sm" onClick={() => p.onDelete([tile.VISUAL_ID])} title="Remove this tile from the dashboard (Delete)">
            <Trash2 size={15} /> Delete
          </button>
        )}
        <button className="vdo-ib sm" aria-label="Close" onClick={p.onClearSelection}><X size={16} /></button>
      </div>

      <Sec title="Content">
        <div className="dd-seg" role="group" aria-label="Tile content">
          <button aria-pressed={!isReport} disabled={!canEdit} onClick={() => p.onSetContentKind('visual')}>Visual (chart / table)</button>
          <button aria-pressed={isReport} disabled={!canEdit} onClick={() => p.onSetContentKind('report')}>Report (Jasper)</button>
        </div>
        <div className="dd-card" style={{ marginTop: 10 }}>
          <div className="dd-vchip">
            <span className="dd-vico">{isReport ? <FileText size={18} /> : visualTypeIcon(visualInfo?.visual_type)}</span>
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              <div className="dd-ell" style={{ fontSize: 14, fontWeight: 600 }}>{contentName}</div>
              <div className="dd-mono dd-ell" style={{ fontSize: 12.5, color: 'var(--color-text-faint)', marginTop: 2 }}>{contentFile}</div>
            </div>
            {canEdit && (
              <button className="dd-tb out sm" onClick={() => p.onOpenReplace(isReport ? 'report' : 'visual')}>
                {ref ? 'Change' : 'Choose'}
              </button>
            )}
          </div>
          {!isReport && tile.VISUAL_CONFIG_REF && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px 8px 12px', borderTop: '1px solid var(--color-border)' }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-faint)', flex: '1 1 auto' }}>
                Changes to the visual apply everywhere it is used.
              </span>
              <button className="dd-tb link" onClick={() => p.onEditVisual(visualObj)}
                title="Open this visual in the Visual designer to change its chart, data and options">
                <Pencil size={14} /> Edit visual
              </button>
            </div>
          )}
        </div>
      </Sec>

      <Sec title="Tile">
        <label className="dd-lab" htmlFor="dd-tile-title">Tile title</label>
        <input id="dd-tile-title" className="dd-inp" value={tile.VISUAL_NAME} disabled={!canEdit}
          onChange={(e) => p.onFieldChange(tile.VISUAL_ID, 'VISUAL_NAME', e.target.value)} />
        <label className="dd-lab" htmlFor="dd-tile-id" style={{ marginTop: 12 }}>
          Visual ID <span style={{ fontWeight: 400, color: 'var(--color-text-faint)' }}>(unique within the dashboard)</span>
        </label>
        <input id="dd-tile-id" className="dd-inp mono" value={tile.VISUAL_ID} disabled={!canEdit}
          onChange={(e) => {
            const v = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_')
            if (v) p.onFieldChange(tile.VISUAL_ID, 'VISUAL_ID', v)
          }} />
        <div style={{ marginTop: 10 }}>
          <Switch
            on={tile.SHOW_HEADER !== false}
            disabled={!canEdit}
            title="Show title bar on this tile"
            detail="Off: the visual fills the tile with no title bar at run time."
            onChange={(on) => p.onFieldChange(tile.VISUAL_ID, 'SHOW_HEADER', on)}
          />
        </div>
      </Sec>

      <Sec title="Position & size" right="grid of 12 columns">
        <div className="dd-g4">
          {([
            ['X', 'X', 0, 11.75],
            ['Y', 'Y', 0, undefined],
            ['WIDTH', 'Width', 0.5, 12],
            ['HEIGHT', 'Height', 0.5, undefined],
          ] as const).map(([field, label, min, max]) => (
            <div key={field}>
              <label className="dd-lab soft" htmlFor={`dd-p-${field}`}>{label}</label>
              <input
                id={`dd-p-${field}`}
                className="dd-inp mono"
                type="number"
                step={0.25}
                min={min}
                max={max}
                disabled={!canEdit}
                value={tile.POSITION[field]}
                onChange={(e) => {
                  const v = Number(e.target.value)
                  if (e.target.value !== '' && Number.isFinite(v)) p.onBoxChange(tile.VISUAL_ID, field, v)
                }}
              />
            </div>
          ))}
        </div>
        <p className="dd-hint">Or drag on the grid. Snaps to ¼ cell; tiles below are pushed down.</p>
      </Sec>

      <Sec title="Criteria bindings" right={params.length ? `${wired} of ${params.length} wired` : undefined}>
        {!ref ? (
          <p className="dd-hint" style={{ margin: 0 }}>Pick a {isReport ? 'report' : 'visual'} above to see its variables.</p>
        ) : p.visualContent === undefined ? (
          <p className="dd-hint" style={{ margin: 0 }}>Loading the {isReport ? 'report' : 'visual'}…</p>
        ) : p.visualContent === null ? (
          <p className="dd-hint" style={{ margin: 0, color: 'var(--color-error)' }}>
            Couldn&apos;t load <span className="dd-mono">{ref}</span>. Its file may be missing.
          </p>
        ) : params.length === 0 ? (
          <p className="dd-hint" style={{ margin: 0 }}>
            This {isReport ? 'report' : 'visual'} has no prompt variables, so dashboard criteria do not reach it.
          </p>
        ) : (
          <>
            <p className="dd-hint" style={{ margin: '0 0 10px' }}>
              How each {isReport ? 'prompt parameter' : 'variable'} gets its value: the {isReport ? 'report' : 'visual'}&apos;s
              own default, a global criterion, or a fixed value.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {params.map((x) => (
                <BindingRow
                  key={x.name}
                  param={x}
                  canEdit={canEdit}
                  binding={byParam.get(x.name)}
                  globals={p.globalCriteria}
                  onChange={(b) => p.onSetBinding(tile.VISUAL_ID, x.name, b)}
                  onGoToCriteria={p.onGoToCriteria}
                />
              ))}
            </div>
          </>
        )}
      </Sec>
    </>
  )
}

function BindingRow({ param, binding, globals, canEdit, onChange, onGoToCriteria }: {
  param: VisualParam
  binding: DashboardCriterionBinding | undefined
  globals: DashboardGlobalCriterion[]
  canEdit: boolean
  onChange: (next: DashboardCriterionBinding | null) => void
  onGoToCriteria: () => void
}) {
  const mode: DashboardBindType = binding?.BIND_TYPE || 'unbound'
  const gp = binding?.GLOBAL_PARAM || ''
  const missing = mode === 'global' && gp !== '' && !globals.some((g) => g.PARAM_NAME === gp)
  const named = globals.filter((g) => (g.PARAM_NAME || '').trim() !== '')
  const toGlobal = () => {
    const match = named.find((g) => g.PARAM_NAME.toLowerCase() === param.name.toLowerCase()) || named[0]
    onChange({ VISUAL_PARAM: param.name, BIND_TYPE: 'global', GLOBAL_PARAM: match?.PARAM_NAME || '' })
  }
  const inType = param.dataType === 'date' ? 'date' : param.dataType === 'number' ? 'number' : 'text'
  return (
    <div className={`dd-bind${missing ? ' bad' : ''}`}>
      <div className="dd-bind-top">
        <b className="dd-mono">{param.name}</b>
        <span className="meta">{[param.promptLabel, param.dataType, param.operator].filter(Boolean).join(' · ')}</span>
      </div>
      <div className="dd-seg" role="group" aria-label={`Binding for ${param.name}`}>
        <button aria-pressed={mode === 'unbound'} disabled={!canEdit} onClick={() => onChange(null)}>Use default</button>
        <button aria-pressed={mode === 'global'} disabled={!canEdit} onClick={toGlobal}>Global</button>
        <button aria-pressed={mode === 'constant'} disabled={!canEdit}
          onClick={() => onChange({ VISUAL_PARAM: param.name, BIND_TYPE: 'constant', CONSTANT_VALUE: '' })}>Constant</button>
      </div>
      {mode === 'global' && (
        named.length === 0 && !missing ? (
          <p className="dd-hint" style={{ margin: 0 }}>
            No global criteria yet. <button className="dd-tb link" style={{ height: 'auto', padding: 0 }} onClick={onGoToCriteria}>Add one on Global criteria</button>
          </p>
        ) : (
          <>
            <div className="dd-pills">
              {named.map((g) => (
                <button key={g.PARAM_NAME} className="dd-pill" aria-pressed={gp === g.PARAM_NAME} disabled={!canEdit}
                  onClick={() => onChange({ VISUAL_PARAM: param.name, BIND_TYPE: 'global', GLOBAL_PARAM: g.PARAM_NAME })}>
                  {g.PROMPT_LABEL || g.PARAM_NAME}
                  {g.PROMPT_LABEL && <span className="pn">{g.PARAM_NAME}</span>}
                </button>
              ))}
              {missing && <span className="dd-pill miss">{gp} <span className="pn">(missing)</span></span>}
            </div>
            {missing && (
              <p className="dd-hint" style={{ margin: 0, color: 'var(--color-error)' }}>
                This global no longer exists. Pick another or add it on Global criteria.
              </p>
            )}
          </>
        )
      )}
      {mode === 'constant' && (
        <input className="dd-inp" type={inType} value={binding?.CONSTANT_VALUE || ''} disabled={!canEdit}
          placeholder={`Constant ${param.dataType} value`} aria-label={`Constant value for ${param.name}`}
          onChange={(e) => onChange({ VISUAL_PARAM: param.name, BIND_TYPE: 'constant', CONSTANT_VALUE: e.target.value })} />
      )}
      {mode === 'unbound' && (
        <p className="dd-hint" style={{ margin: 0 }}>Uses the visual&apos;s own default or prompt.</p>
      )}
    </div>
  )
}
